'use strict';

require('./horizontal_vehicle_picker.scss');

const { handleGarageData } = require('public/js/components/garage-store.js');
const flyouts = require('public/js/elements/flyouts.js');
const {
	fetchEngines,
	fetchMakes,
	fetchModels,
	fetchTrims,
	setVehicle,
} = require('public/js/api/vehicle');
const { shouldRefreshAfterVehicleSelection } = require('public/js/lib/should-refresh-after-vehicle-selection');
const runScriptOnReady = require('public/js/lib/run-script-on-ready');

const STEPS = ['year', 'make', 'model', 'trim', 'engine'];
const MOBILE_LAYOUT_QUERY = '(max-width: 767px)';
const BLOCK = 'horizontal-vehicle-picker';
const FIELD_SELECTOR = `.${BLOCK}__field`;
const CONTROL_SELECTOR = `.${BLOCK}__control`;
const FIELD_OPEN_CLASS = `${BLOCK}__field--open`;
const FILTER_CLASS = `${BLOCK}__filter`;
const MENU_OPEN_CLASS = `${BLOCK}__menu--open`;
const OPTION_CLASS = `${BLOCK}__option`;
const OPTION_SELECTED_CLASS = `${BLOCK}__option--selected`;
const OPTION_SELECTOR = `.${OPTION_CLASS}`;

/** @type {WeakMap<HTMLSelectElement, { field: HTMLElement, filter: HTMLInputElement | null, form: HTMLFormElement, listSignature: string | undefined, listbox: HTMLUListElement, menu: HTMLElement, prompt: string, select: HTMLSelectElement, trigger: HTMLButtonElement, valueEl: HTMLSpanElement }>} */
const fieldMenusBySelect = new WeakMap();

/** @type {Set<{ field: HTMLElement, filter: HTMLInputElement | null, form: HTMLFormElement, listSignature: string | undefined, listbox: HTMLUListElement, menu: HTMLElement, prompt: string, select: HTMLSelectElement, trigger: HTMLButtonElement, valueEl: HTMLSpanElement }>} */
const openFieldMenus = new Set();

/** @type {WeakMap<HTMLSelectElement, Promise<'aborted' | 'auto-selected' | 'loaded'>>} */
const loadOptionsPromises = new WeakMap();

let outsideMenuCloseBound = false;

const FETCHERS = {
	engine: fetchEngines,
	make: fetchMakes,
	model: fetchModels,
	trim: fetchTrims,
};

/**
 * @param {object|null|undefined} selectResponse
 * @returns {Promise<void>}
 */
function redirectOrReloadAfterVehicleSelect(selectResponse) {
	return handleGarageData.invalidateAndRefetchGarage().then((refetch) => {
		if (!shouldRefreshAfterVehicleSelection()) {
			flyouts.closeAllFlyouts();
			if (!refetch.ok) {
				globalThis.location.reload();
			}
			return;
		}

		const redirectUrl = selectResponse?.redirect_url;
		if (redirectUrl) {
			if (typeof globalThis.rp_app?.location?.redirect === 'function') {
				globalThis.rp_app.location.redirect(redirectUrl);
			} else {
				globalThis.location.href = redirectUrl;
			}
			return;
		}

		globalThis.location.reload();
	});
}

function isMobileLayout() {
	if (typeof window.matchMedia !== 'function') {
		return false;
	}

	return window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
}

function getFieldForStep(form, step) {
	const select = form.querySelector(`[data-vehicle-step="${step}"]`);
	if (!select) {
		return null;
	}

	return select.closest(FIELD_SELECTOR);
}

function getFieldMenu(select) {
	return fieldMenusBySelect.get(select) ?? null;
}

function syncFieldMenuDisplay(select) {
	const menuState = getFieldMenu(select);
	if (!menuState) {
		return;
	}

	const option = select.options[select.selectedIndex];
	const selectedLabel = option && option.value !== '' ? option.text.trim() : '';
	if (selectedLabel !== '') {
		menuState.valueEl.textContent = selectedLabel;
		return;
	}

	if (menuState.menu.classList.contains(MENU_OPEN_CLASS)) {
		menuState.valueEl.textContent = menuState.prompt;
		return;
	}

	menuState.valueEl.textContent = '';
}

function syncAllFieldMenus(form) {
	form.querySelectorAll('[data-vehicle-step]').forEach((element) => {
		if (element instanceof HTMLSelectElement) {
			const menuState = getFieldMenu(element);
			if (menuState) {
				menuState.trigger.disabled = element.disabled;
				menuState.trigger.setAttribute(
					'aria-expanded',
					menuState.menu.classList.contains(MENU_OPEN_CLASS) ? 'true' : 'false',
				);
			}
			syncFieldMenuDisplay(element);
		}
	});
}

function countSelectableOptions(select) {
	let count = 0;
	for (const option of select.options) {
		if (option.value !== '') {
			count += 1;
		}
	}

	return count;
}

function getSelectOptionsSignature(select) {
	const parts = [];
	for (const option of select.options) {
		parts.push(`${option.value}\u0000${option.text}`);
	}

	return parts.join('\u0001');
}

function rebuildFieldMenuList(menuState) {
	const fragment = document.createDocumentFragment();
	Array.from(menuState.select.options).forEach((option) => {
		if (option.value === '') {
			return;
		}

		const item = document.createElement('li');
		const button = document.createElement('button');
		button.type = 'button';
		button.className = OPTION_CLASS;
		button.setAttribute('role', 'option');
		button.dataset.value = option.value;
		button.textContent = option.text;
		const isSelected = option.selected || option.value === menuState.select.value;
		button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
		if (isSelected) {
			button.classList.add(OPTION_SELECTED_CLASS);
		}
		item.appendChild(button);
		fragment.appendChild(item);
	});
	menuState.listbox.replaceChildren(fragment);
	menuState.listSignature = getSelectOptionsSignature(menuState.select);
}

function ensureFieldMenuListBuilt(menuState) {
	const signature = getSelectOptionsSignature(menuState.select);
	if (menuState.listSignature === signature) {
		return;
	}

	rebuildFieldMenuList(menuState);
}

function applyFieldMenuFilter(menuState) {
	if (!(menuState.filter instanceof HTMLInputElement)) {
		return;
	}

	const query = menuState.filter.value.trim().toLowerCase();
	menuState.listbox.querySelectorAll('li').forEach((item) => {
		const option = item.querySelector(OPTION_SELECTOR);
		const label = option?.textContent?.trim().toLowerCase() ?? '';
		item.hidden = query !== '' && !label.includes(query);
	});
}

function removeFieldMenuFilter(menuState) {
	if (menuState.filter instanceof HTMLInputElement) {
		menuState.filter.remove();
		menuState.filter = null;
	}
	menuState.listbox.querySelectorAll('li').forEach((item) => {
		item.hidden = false;
	});
}

function closeFieldMenu(menuState) {
	menuState.menu.classList.remove(MENU_OPEN_CLASS);
	menuState.field.classList.remove(FIELD_OPEN_CLASS);
	menuState.trigger.setAttribute('aria-expanded', 'false');
	openFieldMenus.delete(menuState);
	removeFieldMenuFilter(menuState);
	syncFieldMenuDisplay(menuState.select);
}

function closeAllFieldMenus(form, except = null) {
	form.querySelectorAll('[data-vehicle-step]').forEach((element) => {
		if (!(element instanceof HTMLSelectElement)) {
			return;
		}

		const menuState = getFieldMenu(element);
		if (menuState && menuState !== except) {
			closeFieldMenu(menuState);
		}
	});
}

function getVisibleFieldMenuOptions(menuState) {
	return Array.from(menuState.listbox.querySelectorAll(OPTION_SELECTOR)).filter((option) => {
		const listItem = option.closest('li');
		return listItem instanceof HTMLElement && !listItem.hidden;
	});
}

function continueAfterStepSelection(form, step) {
	const next = nextStep(form, step);
	if (next) {
		void loadOptions(form, next, { focusAfterLoad: true });
		return;
	}

	selectVehicle(form);
}

function selectFieldMenuValue(menuState, value) {
	if (value === '') {
		return;
	}

	const { form, select } = menuState;
	const alreadySelected = select.value === value;
	if (!alreadySelected) {
		select.value = value;
		select.dispatchEvent(new Event('change', { bubbles: true }));
	}

	closeFieldMenu(menuState);
	syncFieldMenuDisplay(select);

	if (alreadySelected && !isPickerLocked(form)) {
		continueAfterStepSelection(form, select.dataset.vehicleStep ?? '');
	}
}

function showFieldMenuFilter(menuState, initialCharacter) {
	if (!(menuState.filter instanceof HTMLInputElement)) {
		const filter = document.createElement('input');
		filter.type = 'text';
		filter.className = FILTER_CLASS;
		filter.setAttribute('autocomplete', 'off');
		menuState.menu.insertBefore(filter, menuState.listbox);
		menuState.filter = filter;
		filter.addEventListener('input', () => applyFieldMenuFilter(menuState));
		filter.addEventListener('keydown', (event) => {
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				getVisibleFieldMenuOptions(menuState)[0]?.focus();
			}
			if (event.key === 'Escape') {
				event.preventDefault();
				closeFieldMenu(menuState);
				menuState.trigger.focus();
			}
		});
	}

	menuState.filter.value = initialCharacter;
	applyFieldMenuFilter(menuState);
	menuState.filter.focus();
}

function getOptionsDependencyKey(form, step) {
	return STEPS
		.slice(0, STEPS.indexOf(step))
		.map((dependency) => getSelections(form)[dependency])
		.join('|');
}

function hasCachedOptionsForSelection(form, select) {
	const step = select.dataset.vehicleStep ?? '';
	if (step === 'year') {
		return true;
	}

	return select.dataset.loadedFor === getOptionsDependencyKey(form, step);
}

function applyPreloadedOptionsFromHtml(form) {
	const raw = form.dataset.preloadedOptions;
	if (raw === undefined || raw === '') {
		return;
	}

	let preloaded;
	try {
		preloaded = JSON.parse(raw);
	} catch {
		return;
	}

	if (preloaded === null || typeof preloaded !== 'object' || Array.isArray(preloaded)) {
		return;
	}

	STEPS.forEach((step) => {
		if (step === 'year') {
			return;
		}

		const optionList = preloaded[step];
		if (!Array.isArray(optionList)) {
			return;
		}

		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		if (!(select instanceof HTMLSelectElement)) {
			return;
		}

		const dependencyKey = getOptionsDependencyKey(form, step);
		if (dependencyKey === '' && step !== 'make') {
			return;
		}

		setOptions(select, optionList, select.value);
		select.dataset.loadedFor = dependencyKey;
	});
}

function ensureFieldMenuOptionsLoaded(form, select) {
	if (hasCachedOptionsForSelection(form, select)) {
		return Promise.resolve();
	}

	return loadOptions(form, select.dataset.vehicleStep ?? '', {
		allowAutoAdvance: false,
		focusAfterLoad: false,
	});
}

function revealFieldMenu(menuState, select, form) {
	closeAllFieldMenus(form, menuState);
	ensureFieldMenuListBuilt(menuState);
	menuState.menu.classList.add(MENU_OPEN_CLASS);
	menuState.field.classList.add(FIELD_OPEN_CLASS);
	menuState.trigger.setAttribute('aria-expanded', 'true');
	openFieldMenus.add(menuState);
	syncFieldMenuDisplay(select);
	menuState.trigger.focus({ preventScroll: true });
}

function openFieldMenu(select) {
	const menuState = getFieldMenu(select);
	const form = select.closest(`.${BLOCK}`);
	if (!menuState || !(form instanceof HTMLFormElement) || select.disabled || form.dataset.locked === 'true') {
		return Promise.resolve();
	}

	if (select.dataset.vehicleStep === 'year') {
		revealFieldMenu(menuState, select, form);
		return Promise.resolve();
	}

	return ensureFieldMenuOptionsLoaded(form, select).then(() => {
		revealFieldMenu(menuState, select, form);
	});
}

function isPrintableKey(event) {
	if (event.ctrlKey || event.metaKey || event.altKey) {
		return false;
	}

	return event.key.length === 1;
}

function handleMenuTypeahead(menuState, event) {
	if (!menuState.menu.classList.contains(MENU_OPEN_CLASS)) {
		return;
	}

	if (event.target instanceof HTMLInputElement && event.target.classList.contains(FILTER_CLASS)) {
		return;
	}

	if (!isPrintableKey(event)) {
		return;
	}

	event.preventDefault();
	showFieldMenuFilter(menuState, event.key);
}

function wireFieldMenuTrigger(menuState) {
	const { form, select, trigger } = menuState;

	trigger.addEventListener('mousedown', (event) => {
		if (event.button !== 0 || select.disabled || form.dataset.locked === 'true') {
			return;
		}

		event.preventDefault();
		if (menuState.menu.classList.contains(MENU_OPEN_CLASS)) {
			closeFieldMenu(menuState);
			return;
		}

		void openFieldMenu(select);
	});

	trigger.addEventListener('focus', () => {
		if (select.disabled || form.dataset.locked === 'true' || menuState.menu.classList.contains(MENU_OPEN_CLASS)) {
			return;
		}

		if (countSelectableOptions(select) === 0) {
			return;
		}

		void openFieldMenu(select);
	});

	trigger.addEventListener('keydown', (event) => {
		if (select.disabled || form.dataset.locked === 'true') {
			return;
		}

		if (!menuState.menu.classList.contains(MENU_OPEN_CLASS)) {
			if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				void openFieldMenu(select);
			}
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			closeFieldMenu(menuState);
			return;
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			getVisibleFieldMenuOptions(menuState)[0]?.focus();
			return;
		}

		handleMenuTypeahead(menuState, event);
	});

	menuState.field.addEventListener('keydown', (event) => {
		if (select.disabled || form.dataset.locked === 'true') {
			return;
		}

		handleMenuTypeahead(menuState, event);
	});

	menuState.listbox.addEventListener('mousedown', (event) => {
		const target = event.target;
		if (!(target instanceof HTMLButtonElement) || !target.matches(OPTION_SELECTOR)) {
			return;
		}

		event.preventDefault();
		selectFieldMenuValue(menuState, target.dataset.value ?? '');
	});

	menuState.listbox.addEventListener('keydown', (event) => {
		if (!(event.target instanceof HTMLButtonElement) || !event.target.matches(OPTION_SELECTOR)) {
			return;
		}

		const visible = getVisibleFieldMenuOptions(menuState);
		const currentIndex = visible.indexOf(event.target);
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			visible[currentIndex + 1]?.focus();
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			if (currentIndex <= 0) {
				if (menuState.filter instanceof HTMLInputElement) {
					menuState.filter.focus();
				} else {
					trigger.focus();
				}
				return;
			}
			visible[currentIndex - 1]?.focus();
		}
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			selectFieldMenuValue(menuState, event.target.dataset.value ?? '');
		}
		if (event.key === 'Escape') {
			event.preventDefault();
			closeFieldMenu(menuState);
			trigger.focus();
		}
	});
}

function bindOutsideMenuClose() {
	if (outsideMenuCloseBound) {
		return;
	}

	outsideMenuCloseBound = true;
	document.addEventListener('mousedown', (event) => {
		if (!(event.target instanceof Node) || openFieldMenus.size === 0) {
			return;
		}

		openFieldMenus.forEach((menuState) => {
			if (!menuState.field.contains(event.target)) {
				closeFieldMenu(menuState);
			}
		});
	});
}

function wireFieldMenus(form) {
	bindOutsideMenuClose();

	form.querySelectorAll('[data-vehicle-step]').forEach((element) => {
		if (!(element instanceof HTMLSelectElement)) {
			return;
		}

		const select = element;
		const field = select.closest(FIELD_SELECTOR);
		const control = select.closest(CONTROL_SELECTOR);
		const trigger = control?.querySelector(`.${BLOCK}__trigger`);
		const menu = control?.querySelector(`.${BLOCK}__menu`);
		const listbox = control?.querySelector(`.${BLOCK}__listbox`);
		const valueEl = trigger?.querySelector(`.${BLOCK}__value`);
		if (!(field instanceof HTMLElement)
			|| !(control instanceof HTMLElement)
			|| !(trigger instanceof HTMLButtonElement)
			|| !(menu instanceof HTMLElement)
			|| !(listbox instanceof HTMLUListElement)
			|| !(valueEl instanceof HTMLSpanElement)) {
			return;
		}

		const prompt = select.dataset.stepPrompt ?? `Select ${select.name}`;
		const menuState = {
			field,
			filter: null,
			form,
			listSignature: undefined,
			listbox,
			menu,
			prompt,
			select,
			trigger,
			valueEl,
		};
		fieldMenusBySelect.set(select, menuState);
		wireFieldMenuTrigger(menuState);
		syncFieldMenuDisplay(select);
	});
}

function syncFieldStates(form) {
	const mobile = isMobileLayout();
	let lastFilledIndex = -1;

	STEPS.forEach((step, index) => {
		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		const field = getFieldForStep(form, step);
		if (!select || !field || field.hasAttribute('hidden')) {
			return;
		}

		if (select.value !== '') {
			lastFilledIndex = index;
		}
	});

	STEPS.forEach((step, index) => {
		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		const field = getFieldForStep(form, step);
		if (!select || !field || field.hasAttribute('hidden')) {
			return;
		}

		field.classList.toggle(`${BLOCK}__field--filled`, select.value !== '');
		field.classList.toggle(`${BLOCK}__field--disabled`, select.disabled);

		const selectedOption = select.options[select.selectedIndex];
		const selectedLabel = selectedOption?.text?.trim() ?? '';
		if (select.value !== '' && selectedLabel !== '') {
			select.title = selectedLabel;
		} else {
			select.removeAttribute('title');
		}

		if (mobile) {
			const visibleThrough = lastFilledIndex + 1;
			field.classList.toggle(`${BLOCK}__field--visible`, index <= visibleThrough);
		} else {
			field.classList.add(`${BLOCK}__field--visible`);
		}
	});

	syncSelectionCompleteAttribute(form);
	syncAllFieldMenus(form);
}

function isPickerLocked(form) {
	return form.dataset.locked === 'true';
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function isSelectionComplete(form) {
	const requiresFitmentDetails = form.dataset.requiresFitmentDetails === 'true';

	return STEPS.every((step) => {
		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		const field = getFieldForStep(form, step);
		if (!select || !field || field.hasAttribute('hidden')) {
			return true;
		}

		if (!requiresFitmentDetails && (step === 'trim' || step === 'engine')) {
			return true;
		}

		return select.value !== '';
	});
}

/**
 * @param {HTMLFormElement} form
 * @param {boolean} locked
 */
function setPickerLocked(form, locked) {
	if (locked) {
		form.dataset.locked = 'true';
		form.classList.add(`${BLOCK}--locked`);
		closeAllFieldMenus(form);
	} else {
		delete form.dataset.locked;
		form.classList.remove(`${BLOCK}--locked`);
	}

	STEPS.forEach((step) => {
		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		if (select) {
			select.disabled = locked;
		}
	});

	syncFieldStates(form);
}

function syncSelectEnabledState(form) {
	if (isPickerLocked(form)) {
		STEPS.forEach((step) => {
			const select = form.querySelector(`[data-vehicle-step="${step}"]`);
			if (select) {
				select.disabled = true;
			}
		});
		syncFieldStates(form);
		return;
	}

	STEPS.forEach((step, index) => {
		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		const field = getFieldForStep(form, step);
		if (!select || !field || field.hasAttribute('hidden')) {
			return;
		}

		if (index === 0) {
			select.disabled = false;
			return;
		}

		const previousStep = STEPS[index - 1];
		const previousSelect = form.querySelector(`[data-vehicle-step="${previousStep}"]`);
		select.disabled = previousSelect === null || previousSelect.value === '';
	});

	syncFieldStates(form);
}

function getSelections(form) {
	return STEPS.reduce((selections, step) => {
		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		selections[step] = select?.value ?? '';

		return selections;
	}, {});
}

function bumpLoadGeneration(form) {
	const next = String(Number(form.dataset.loadGeneration || 0) + 1);
	form.dataset.loadGeneration = next;

	return next;
}

function syncSelectionCompleteAttribute(form) {
	form.dataset.vehicleSelectionComplete = isSelectionComplete(form) ? 'true' : 'false';
}

function resetFollowingSteps(form, changedStep) {
	bumpLoadGeneration(form);
	const changedIndex = STEPS.indexOf(changedStep);
	STEPS.slice(changedIndex + 1).forEach((step) => {
		const select = form.querySelector(`[data-vehicle-step="${step}"]`);
		if (!select) {
			return;
		}

		select.replaceChildren(select.options[0]);
		select.disabled = true;
		delete select.dataset.loadedFor;
		delete select.dataset.loading;
		loadOptionsPromises.delete(select);
		const menuState = getFieldMenu(select);
		if (menuState) {
			menuState.listSignature = undefined;
			if (menuState.menu.classList.contains(MENU_OPEN_CLASS)) {
				closeFieldMenu(menuState);
			}
		}
	});
	syncSelectEnabledState(form);
}

function resolveSelectedOptionValue(select, selectedValue) {
	if (selectedValue === '') {
		return '';
	}

	const options = Array.from(select.options).filter((option) => option.value !== '');
	if (options.some((option) => option.value === selectedValue)) {
		return selectedValue;
	}

	const normalized = selectedValue.toLowerCase();
	const match = options.find((option) => option.value.toLowerCase() === normalized);

	return match?.value ?? selectedValue;
}

function setOptions(select, options, selectedValue = '') {
	const placeholder = select.options[0];
	const valueToKeep = selectedValue !== '' ? selectedValue : select.value;
	select.replaceChildren(placeholder);

	options.forEach((option) => {
		const element = document.createElement('option');
		element.textContent = option.ui ?? option.label ?? '';
		element.value = option.url ?? option.id ?? '';
		select.appendChild(element);
	});

	const resolvedValue = resolveSelectedOptionValue(select, valueToKeep);
	if (resolvedValue !== '' && Array.from(select.options).some((option) => option.value === resolvedValue)) {
		select.value = resolvedValue;
	}

	const pickerForm = select.closest(`.${BLOCK}`);
	if (pickerForm && !isPickerLocked(pickerForm)) {
		select.disabled = false;
		syncSelectEnabledState(pickerForm);
	}

	const menuState = getFieldMenu(select);
	if (menuState) {
		menuState.listSignature = undefined;
		if (menuState.menu.classList.contains(MENU_OPEN_CLASS)) {
			rebuildFieldMenuList(menuState);
		}
	}
	syncFieldMenuDisplay(select);
}

function nextStep(form, step) {
	const requiresFitmentDetails = form.dataset.requiresFitmentDetails === 'true';
	if (!requiresFitmentDetails && step === 'model') {
		return null;
	}

	const nextIndex = STEPS.indexOf(step) + 1;

	return STEPS[nextIndex] ?? null;
}

function activateFieldStep(select, openMenu) {
	if (!(select instanceof HTMLSelectElement) || select.disabled) {
		return;
	}

	const menuState = getFieldMenu(select);
	const form = select.closest(`.${BLOCK}`);
	if (!menuState || !(form instanceof HTMLFormElement)) {
		return;
	}

	menuState.trigger.focus();
	if (openMenu && countSelectableOptions(select) > 0) {
		void openFieldMenu(select);
	}
}

/**
 * @param {{
 *   allowAutoAdvance: boolean,
 *   dependencyKey: string,
 *   form: HTMLFormElement,
 *   options: Record<string, { id?: string, url?: string }> | null | undefined,
 *   requestGeneration: string,
 *   select: HTMLSelectElement,
 *   selectedValue: string,
 *   step: string,
 * }} loadResult
 * @returns {'aborted' | 'auto-selected' | 'loaded'}
 */
function finalizeLoadedOptions(loadResult) {
	const {
		allowAutoAdvance,
		dependencyKey,
		form,
		options,
		requestGeneration,
		select,
		selectedValue,
		step,
	} = loadResult;

	if ((form.dataset.loadGeneration || '0') !== requestGeneration) {
		delete select.dataset.loading;
		return 'aborted';
	}

	const currentDependencyKey = getOptionsDependencyKey(form, step);
	if (currentDependencyKey !== dependencyKey) {
		delete select.dataset.loading;
		return 'aborted';
	}

	const optionList = Object.values(options ?? {});
	setOptions(select, optionList, selectedValue);
	select.dataset.loadedFor = dependencyKey;
	delete select.dataset.loading;

	const isBootstrapping = form.dataset.bootstrapping === 'true';
	if (allowAutoAdvance && optionList.length === 0 && step === 'trim' && !isBootstrapping) {
		selectVehicle(form);
		return 'aborted';
	}
	if (allowAutoAdvance && optionList.length === 1 && !isBootstrapping) {
		select.value = optionList[0].url ?? optionList[0].id ?? '';
		select.dispatchEvent(new Event('change', { bubbles: true }));
		return 'auto-selected';
	}

	return 'loaded';
}

/**
 * @param {HTMLFormElement} form
 * @param {string} step
 * @param {{ allowAutoAdvance?: boolean, focusAfterLoad?: boolean }} [loadConfig]
 * @returns {Promise<void>}
 */
function loadOptions(form, step, loadConfig = {}) {
	const focusAfterLoad = loadConfig.focusAfterLoad === true;
	const allowAutoAdvance = loadConfig.allowAutoAdvance !== false;
	const select = form.querySelector(`[data-vehicle-step="${step}"]`);
	const fetcher = FETCHERS[step];
	if (!select || !fetcher || isPickerLocked(form)) {
		return Promise.resolve();
	}

	const pendingLoad = loadOptionsPromises.get(select);
	if (pendingLoad !== undefined) {
		return pendingLoad.then((result) => {
			if (focusAfterLoad && result === 'loaded') {
				activateFieldStep(select, true);
			}
		});
	}

	const dependencyKey = getOptionsDependencyKey(form, step);
	if (select.dataset.loadedFor === dependencyKey) {
		if (focusAfterLoad) {
			activateFieldStep(select, true);
		}
		return Promise.resolve();
	}

	const requestGeneration = form.dataset.loadGeneration || '0';
	select.dataset.loading = 'true';
	const selectedValue = select.value;

	const loadPromise = new Promise((resolve) => {
		fetcher(
			getSelections(form),
			(fetchedOptions) => {
				resolve(finalizeLoadedOptions({
					allowAutoAdvance,
					dependencyKey,
					form,
					options: fetchedOptions,
					requestGeneration,
					select,
					selectedValue,
					step,
				}));
			},
			() => {
				if ((form.dataset.loadGeneration || '0') === requestGeneration) {
					delete select.dataset.loading;
				}
				resolve('aborted');
			},
		);
	}).finally(() => {
		loadOptionsPromises.delete(select);
	});

	loadOptionsPromises.set(select, loadPromise);

	return loadPromise.then((result) => {
		if (focusAfterLoad && result === 'loaded') {
			activateFieldStep(select, true);
		}
	});
}

function showError(form, message) {
	const error = form.querySelector(`.${BLOCK}__error`);
	error.textContent = message;
	error.hidden = false;
}

function selectVehicle(form) {
	if (form.dataset.submitting === 'true' || isPickerLocked(form)) {
		return;
	}

	const selections = getSelections(form);
	const error = form.querySelector(`.${BLOCK}__error`);
	error.hidden = true;
	form.dataset.submitting = 'true';
	setPickerLocked(form, true);

	setVehicle(
		{
			...selections,
			page: rp_app.env?.page ?? '',
			path: rp_app.location?.pathname ?? window.location.pathname,
		},
		(xhr) => {
			showError(form, xhr?.responseJSON?.message ?? 'Unable to select vehicle.');
			delete form.dataset.submitting;
			setPickerLocked(form, false);
		},
		(data) => {
			void redirectOrReloadAfterVehicleSelect(data).finally(() => {
				delete form.dataset.submitting;
			});
		},
	);
}

/**
 * After reload with server defaults, enable the chain and preload the next empty step.
 *
 * @param {HTMLFormElement} form
 */
async function bootstrapPickerFromDefaults(form) {
	delete form.dataset.submitting;
	form.dataset.bootstrapping = 'true';
	syncSelectEnabledState(form);

	try {
		if (isSelectionComplete(form)) {
			setPickerLocked(form, true);
			return;
		}

		for (let index = 0; index < STEPS.length; index += 1) {
			const step = STEPS[index];
			const select = form.querySelector(`[data-vehicle-step="${step}"]`);
			const field = getFieldForStep(form, step);
			if (!select || !field || field.hasAttribute('hidden')) {
				continue;
			}

			if (select.value === '') {
				if (index > 0) {
					await loadOptions(form, step, { focusAfterLoad: false });
				}
				return;
			}
		}
	} finally {
		delete form.dataset.bootstrapping;
		syncSelectionCompleteAttribute(form);
	}
}

function initializePicker(form) {
	wireFieldMenus(form);
	delete form.dataset.submitting;
	form.dataset.loadGeneration = '0';
	form.dataset.vehicleSelectionComplete = 'false';
	applyPreloadedOptionsFromHtml(form);
	void bootstrapPickerFromDefaults(form);

	form.addEventListener('change', (event) => {
		const select = event.target.closest('[data-vehicle-step]');
		if (!select || isPickerLocked(form)) {
			return;
		}

		const step = select.dataset.vehicleStep;
		if (select.value === '') {
			resetFollowingSteps(form, step);
			return;
		}

		resetFollowingSteps(form, step);
		continueAfterStepSelection(form, step);
	});

	form.addEventListener('submit', (event) => event.preventDefault());

	if (typeof window.matchMedia === 'function') {
		const mediaQuery = window.matchMedia(MOBILE_LAYOUT_QUERY);
		const handleLayoutChange = () => syncFieldStates(form);
		mediaQuery.addEventListener('change', handleLayoutChange);
	}
}

runScriptOnReady('horizontal_vehicle_picker', () => {
	document.querySelectorAll(`.${BLOCK}`).forEach(initializePicker);
});
